import { useState } from "react";
import axios from "axios";
import { Phone, Mail, Globe, Building2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BRAND } from "@/lib/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Our team will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-16 md:py-24 bg-[#f8fafc]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <span className="eyebrow">Contact Us</span>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
              Talk to a real<br /> insurance advisor.
            </h2>
            <p className="font-hindi mt-3 text-lg text-[#1e3a8a] font-semibold">
              अधिक जानकारी के लिए संपर्क करें
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Aapka sawaal chhota ho ya bada — humein call karein, WhatsApp karein, ya neeche form bharein. Hum 24 ghante mein jawab denge.
            </p>

            <div className="mt-8 space-y-4">
              <ContactRow
                icon={Building2}
                label="Head Office"
                value={
                  <div className="not-italic">
                    <div className="font-bold text-slate-900">
                      {BRAND.legalName}
                    </div>
                    <div className="text-slate-600 leading-relaxed">
                      {BRAND.address}
                    </div>
                  </div>
                }
              />
              <ContactRow
                icon={Phone}
                label="Phone"
                value={
                  <div className="flex flex-wrap gap-3">
                    {BRAND.phones.map((p) => (
                      <a
                        key={p}
                        href={`tel:+91${p}`}
                        data-testid={`contact-phone-${p}`}
                        className="font-bold text-slate-900 hover:text-[#1e3a8a]"
                      >
                        +91 {p}
                      </a>
                    ))}
                  </div>
                }
              />
              <ContactRow
                icon={Mail}
                label="Email"
                value={
                  <a
                    data-testid="contact-email"
                    href={`mailto:${BRAND.email}`}
                    className="font-bold text-slate-900 hover:text-[#1e3a8a]"
                  >
                    {BRAND.email}
                  </a>
                }
              />
              <ContactRow
                icon={Globe}
                label="Website"
                value={
                  <a
                    href={`https://${BRAND.website}`}
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold text-slate-900 hover:text-[#1e3a8a]"
                  >
                    {BRAND.website}
                  </a>
                }
              />
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-10"
          >
            <h3 className="font-display text-2xl font-bold text-slate-900">
              Send us a message
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              We typically reply within a few hours.
            </p>

            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <Field label="Your name *" htmlFor="c-name">
                <Input
                  id="c-name"
                  data-testid="contact-name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  placeholder="Rajesh Sharma"
                  className="h-11"
                  required
                />
              </Field>
              <Field label="Email *" htmlFor="c-email">
                <Input
                  id="c-email"
                  data-testid="contact-email-input"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  placeholder="you@example.com"
                  className="h-11"
                  required
                />
              </Field>
              <Field label="Phone" htmlFor="c-phone">
                <Input
                  id="c-phone"
                  data-testid="contact-phone-input"
                  name="phone"
                  value={form.phone}
                  onChange={onChange}
                  placeholder="+91 98XXX XXXXX"
                  className="h-11"
                />
              </Field>
              <Field label="Subject" htmlFor="c-subject">
                <Input
                  id="c-subject"
                  data-testid="contact-subject"
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder="e.g. Truck insurance enquiry"
                  className="h-11"
                />
              </Field>
            </div>

            <Field className="mt-4" label="Message *" htmlFor="c-msg">
              <Textarea
                id="c-msg"
                data-testid="contact-message"
                name="message"
                value={form.message}
                onChange={onChange}
                placeholder="Tell us what you need help with..."
                rows={5}
                required
              />
            </Field>

            <Button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="mt-6 bg-[#1e3a8a] hover:bg-[#172554] text-white font-bold rounded-full px-7 h-12 shadow-md"
            >
              {loading ? "Sending..." : "Send Message"}
              <Send className="w-4 h-4 ml-2" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-[#fef3c7] flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#92400e]" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          {label}
        </div>
        <div className="mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, children, className = "" }) {
  return (
    <div className={className}>
      <Label htmlFor={htmlFor} className="text-sm font-semibold text-slate-700">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
